import React, { useEffect, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import {
  CircularProgress,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { Button } from '@resiliatech/birdsong'
import GET_FUNDER_ORG_COHORT from 'Apollo/Queries/GetFunderOrgCohort'
import { NonprofitOrg } from 'Generated/graphql-types'
import {
  ErrorMessage,
  SelectContainer,
  Text,
  TextFieldContainer,
} from './FunderSharedGoals.styled'
import { FEEDBACK_TIMEOUT } from './constants'
import {
  Buttons,
  FormControlContainer,
  Instructions,
  NonProfitOrgSearchContainer,
} from './NonprofitOrgSearch.styled'

const FUNDER_EMAIL = 'FUNDER_EMAIL'
const FUNDER_ID = 'FUNDER_ID'
const NONPROFIT_ORG_IDS = 'NONPROFIT_ORG_IDS'

type SearchVariables = {
  funderEmail?: string
  funderOrgId?: number
  nonprofitOrgIds: number[]
}

type NonprofitOrgSearchProps = {
  nonprofitOrgs: NonprofitOrg[]
  onChangeSearchValue: (value: string) => void
  onClear: () => void
  onSearch: (funderOrgId: number, nonprofitOrgs: NonprofitOrg[]) => void
  searchValue: string
}

export const NonprofitOrgSearch = ({
  nonprofitOrgs,
  onChangeSearchValue,
  onClear,
  onSearch,
  searchValue,
}: NonprofitOrgSearchProps) => {
  const [error, setError] = useState('')
  const [searchMethod, setSearchMethod] = useState(FUNDER_EMAIL)

  const [getFunderOrgCohort, { loading, error: queryError }] = useLazyQuery(
    GET_FUNDER_ORG_COHORT
  )

  useEffect(() => {
    if (queryError) {
      setError(queryError?.message)
      setTimeout(() => setError(''), FEEDBACK_TIMEOUT)
    }
  }, [queryError])

  const showErrorMessage = (message: string) => {
    setError(message)
    setTimeout(() => setError(''), FEEDBACK_TIMEOUT)
  }

  const validateFunderEmailInput = () => {
    if (!searchValue.includes('@')) {
      showErrorMessage('Please enter a valid email address')
      return null
    }
    return searchValue
  }

  const validateFunderIdInput = () => {
    const funderOrgId = Number(searchValue)
    if (Number.isNaN(funderOrgId)) {
      showErrorMessage('Please enter a valid funder ID')
      return null
    }
    return funderOrgId
  }

  const validateNonprofitOrgIdsInput = () => {
    if (!searchValue) return []

    const ids = searchValue.split(',').map((id) => Number(id))

    if (ids.some((id) => Number.isNaN(id))) {
      showErrorMessage('Please enter valid nonprofit organization IDs')
      return []
    }

    return [...new Set(ids)]
  }

  const handleFunderSearch = async () => {
    setError('')
    if (!searchValue) return

    const variables: SearchVariables = { nonprofitOrgIds: [] }

    switch (searchMethod) {
      case FUNDER_EMAIL: {
        const funderEmail = validateFunderEmailInput()
        if (!funderEmail) return
        variables.funderEmail = searchValue
        break
      }
      case FUNDER_ID: {
        const funderOrgId = validateFunderIdInput()
        if (!funderOrgId) return
        variables.funderOrgId = funderOrgId
        break
      }
      case NONPROFIT_ORG_IDS: {
        const ids = validateNonprofitOrgIdsInput()
        if (!ids.length) return
        variables.nonprofitOrgIds = ids
        break
      }
      default:
        break
    }

    const {
      data: {
        funderOrgCohortQuery: {
          funderOrgId = null,
          nonprofitOrgs: foundOrgs = [],
        } = {},
      } = {},
    } = await getFunderOrgCohort({ variables })

    onSearch(funderOrgId, foundOrgs)
  }

  return (
    <NonProfitOrgSearchContainer>
      <Instructions>
        <Text>
          First, select how you&apos;d like to identify your nonprofit
          organizations. You may manually add them by ID, search by funder ID,
          or search by funder email.
        </Text>
        <Text>
          To search by nonprofit organization IDs, separate each ID with a comma
          (e.g. 42 or 15,27,42).
        </Text>
      </Instructions>
      <FormControlContainer>
        <SelectContainer>
          <InputLabel>Method</InputLabel>
          <Select
            disabled={!!nonprofitOrgs.length}
            displayEmpty
            label='Method'
            onChange={({ target: { value } }) => setSearchMethod(value)}
            required
            sx={{ minWidth: '200px' }}
            value={searchMethod}
          >
            <MenuItem value={FUNDER_EMAIL}>Funder Email</MenuItem>
            <MenuItem value={FUNDER_ID}>Funder ID</MenuItem>
            <MenuItem value={NONPROFIT_ORG_IDS}>Nonprofit Org IDs</MenuItem>
          </Select>
        </SelectContainer>
        <TextFieldContainer>
          <TextField
            disabled={!!nonprofitOrgs.length}
            required
            label={
              searchMethod === NONPROFIT_ORG_IDS
                ? 'Nonprofit Org IDs'
                : 'Search Value'
            }
            value={searchValue}
            sx={{ minWidth: '300px' }}
            onChange={({ target: { value } }) => onChangeSearchValue(value)}
          />
        </TextFieldContainer>
        <Buttons>
          {loading ? (
            <CircularProgress />
          ) : (
            <Button
              isDisabled={!searchValue || !!nonprofitOrgs.length}
              label='Search'
              onClick={handleFunderSearch}
              type='button'
              variant='primary'
            />
          )}
          {!loading && (
            <Button
              label='Clear All'
              onClick={onClear}
              type='button'
              variant='secondary'
            />
          )}
        </Buttons>
      </FormControlContainer>
      {error && <ErrorMessage color='error'>{error}</ErrorMessage>}
    </NonProfitOrgSearchContainer>
  )
}
